import PermissionButton from '../../permissions/PermissionButton';
import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, message, Typography, Input, Select, Checkbox, Space, Spin, Tag, Popconfirm } from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  HomeOutlined,
  MailOutlined,
  GlobalOutlined,
  FileTextOutlined,
  SaveOutlined,
  EditOutlined,
  DeleteOutlined,
  UndoOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useFetchFourById, useUpdateFour, useDeleteFour } from '../../hooks/fourHooks';
import { useCountries } from '../../hooks/listsHooks';

const { Title, Text } = Typography;

const SingleFour = ({ fourId, onChangeView }) => {
  const { Four, loading, error, refetch } = useFetchFourById(fourId);
  const { handleUpdate } = useUpdateFour();
  const { handleDelete } = useDeleteFour();
  const { countries } = useCountries();
  
  const [isEditing, setIsEditing] = useState(false);
  const [localData, setLocalData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (Four) {
      setLocalData({ ...Four });
    }
  }, [Four]);

  const handleFieldChange = (field, value) => {
    setLocalData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!localData) return;
    setSaving(true);
    try {
      await handleUpdate(fourId, localData);
      message.success('Fournisseur mis à jour avec succès');
      setHasChanges(false);
      setIsEditing(false);
      refetch();
    } catch (err) {
      message.error('Erreur lors de la mise à jour: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setLocalData({ ...Four });
    setHasChanges(false);
    setIsEditing(false);
  };

  const handleDeleteFour = async () => {
    try {
      await handleDelete(fourId);
      message.success('Fournisseur supprimé avec succès');
      onChangeView('list');
    } catch (err) {
      message.error('Erreur lors de la suppression: ' + (err.message || err));
    }
  };

  if (loading) return <Spin tip="Chargement..." style={{ display: 'block', margin: '50px auto' }} />;
  if (error) return <p style={{ color: 'red' }}>Erreur: {error}</p>;
  if (!localData) return <p>Aucun fournisseur trouvé</p>;

  const EditableText = ({ label, field, icon, type = 'text' }) => {
    if (isEditing) {
      if (type === 'textarea') {
        return (
          <Input.TextArea
            value={localData[field] || ''}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            rows={2}
            style={{ width: '100%' }}
          />
        );
      }
      return (
        <Input
          value={localData[field] || ''}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          prefix={icon}
          style={{ width: '100%' }}
        />
      );
    }
    return (
      <Text style={{ fontSize: 14 }}>
        {icon && <span style={{ marginRight: 8 }}>{icon}</span>}
        {localData[field] || '-'}
      </Text>
    );
  };

  const EditableSelect = ({ label, field, options, icon }) => {
    if (isEditing) {
      return (
        <Select
          value={localData[field] || undefined}
          onChange={(value) => handleFieldChange(field, value)}
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="children"
          placeholder="Sélectionner..."
        >
          {options.map(opt => (
            <Select.Option key={opt} value={opt}>{opt}</Select.Option>
          ))}
        </Select>
      );
    }
    return (
      <Text style={{ fontSize: 14 }}>
        {icon && <span style={{ marginRight: 8 }}>{icon}</span>}
        {localData[field] || '-'}
      </Text>
    );
  };

  const EditableCheckbox = ({ label, field }) => {
    if (isEditing) {
      return (
        <Checkbox
          checked={localData[field] || false}
          onChange={(e) => handleFieldChange(field, e.target.checked)}
        >
          {label}
        </Checkbox>
      );
    }
    return (
      <Tag color={localData[field] ? 'green' : 'default'}>
        {localData[field] ? 'Oui' : 'Non'}
      </Tag>
    );
  };

  return (
    <div style={{ background: 'white', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Title level={3} style={{ margin: 0 }}>
          Fournisseur: {localData.sociale}
          {localData.compte && (
            <Text type="secondary" style={{ fontSize: 16, marginLeft: 12 }}>
              (Compte: {localData.compte})
            </Text>
          )}
        </Title>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => onChangeView && onChangeView('list')}
          >
            Retour à la liste
          </Button>
          {!isEditing ? (
            <>
              <PermissionButton permissionModule="suppliers" permissionAction="update"
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setIsEditing(true)}
              >
                Modifier
              </PermissionButton>
              <Popconfirm
                title="Supprimer ce fournisseur?"
                description="Cette action est irréversible."
                onConfirm={handleDeleteFour}
                okText="Oui, supprimer"
                cancelText="Annuler"
                okButtonProps={{ danger: true }}
              >
                <PermissionButton permissionModule="suppliers" permissionAction="delete" danger icon={<DeleteOutlined />}>
                  Supprimer
                </PermissionButton>
              </Popconfirm>
            </>
          ) : (
            <>
              <Button
                icon={<UndoOutlined />}
                onClick={handleCancel}
                disabled={saving}
              >
                Annuler
              </Button>
              <PermissionButton permissionModule="suppliers" permissionAction="update"
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
                disabled={!hasChanges}
              >
                Enregistrer
              </PermissionButton>
            </>
          )}
        </Space>
      </div>

      <Card
        style={{ marginBottom: 16 }}
        title="Informations Générales"
        extra={
          <Tag color={localData.bloquer ? 'red' : 'green'}>
            {localData.bloquer ? (
              <><CloseCircleOutlined /> Bloqué</>
            ) : (
              <><CheckCircleOutlined /> Actif</>
            )}
          </Tag>
        }
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong style={{ color: '#666' }}>Compte Comptable</Text>
            </div>
            <EditableText field="compte" icon={<HomeOutlined />} />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong style={{ color: '#666' }}>Raison Sociale</Text>
            </div>
            <EditableText field="sociale" icon={<UserOutlined />} />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong style={{ color: '#666' }}>Pays</Text>
            </div>
            <EditableSelect field="pays" options={countries || []} icon={<GlobalOutlined />} />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong style={{ color: '#666' }}>Email</Text>
            </div>
            <EditableText field="email" icon={<MailOutlined />} />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong style={{ color: '#666' }}>Téléphone</Text>
            </div>
            <EditableText field="tel" icon={<PhoneOutlined />} />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong style={{ color: '#666' }}>Description</Text>
            </div>
            <EditableText field="desc" icon={<FileTextOutlined />} />
          </Col>
        </Row>
      </Card>

      <Card title="Options & Notes">
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong style={{ color: '#666' }}>Paiement à Échéance</Text>
            </div>
            <EditableCheckbox label="Activer l'échéance" field="echeance" />
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong style={{ color: '#666' }}>Condition de Paiement</Text>
            </div>
            <EditableCheckbox label="Condition particulière" field="cond_paie" />
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong style={{ color: '#666' }}>Blocage</Text>
            </div>
            {isEditing ? (
              <Checkbox
                checked={localData.bloquer || false}
                onChange={(e) => handleFieldChange('bloquer', e.target.checked)}
              >
                Bloquer ce fournisseur
              </Checkbox>
            ) : (
              <Tag color={localData.bloquer ? 'red' : 'green'}>
                {localData.bloquer ? 'Bloqué' : 'Non bloqué'}
              </Tag>
            )}
          </Col>
          <Col xs={24}>
            <div style={{ marginBottom: 4 }}>
              <Text strong style={{ color: '#666' }}>Note</Text>
            </div>
            <EditableText field="note" type="textarea" />
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default SingleFour;
